import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import { useForm, useWatch } from 'react-hook-form';
import {
    AutocompleteElement,
    CheckboxElement,
    FormContainer,
    TextFieldElement,
} from 'react-hook-form-mui';
import {
    Card,
    CardActions,
    CardContent,
    CardHeader,
    Stack,
} from '@mui/material';

import {
    BaseProductInput,
    BrandInput,
    EditButtons,
    ImageField,
    ProductLineInput,
    ProductSubtypeInput,
    ProductTypeInput,
} from '@barboxx/admin-components';
import { PRODUCT_SCHEMA } from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { Product } from '@barboxx/models';
import { Can, useBool } from '@barboxx/react-utils';
import {
    FLAVOR_TYPES,
    ImageKitCustomMetadataKeys,
    ImageKitMenuItemFolders,
    ImageKitNamedTransforms,
    ImageKitTypes,
} from '@barboxx/types';
import { enumToList } from '@barboxx/utils';

import { REACT_APP_ENV } from '../../../../../environments/environment';

interface Props {
    product: Product;
}

export const ProductDetailsCard = ({ product }: Props) => {
    const [editable, editableActions] = useBool(false);
    const notifications = useNotifications();

    const mutateUser = useMutation({
        mutationKey: ['product', product.id],
        mutationFn: (data: Product.UpdateRequest) =>
            Api.Products.Products.putProduct(product.id, data),
    });

    const formContext = useForm<Product.UpdateRequest>({
        resolver: yupResolver(PRODUCT_SCHEMA) as any,
        defaultValues: product,
    });

    const handleCancel = () => {
        editableActions.off();
        formContext.reset(undefined);
    };

    const onSubmit = async ({
        // brand,
        // productType,
        // productSubtype,
        // flavorVariants,
        // attributeValues,
        // deleted,
        ...values
    }: Product.UpdateRequest) => {
        mutateUser
            .mutateAsync(values)
            .then(({ data }) => {
                notifications.show('Updated product!', {
                    severity: 'success',
                });

                editableActions.off();
                formContext.reset(data);
            })
            .catch(e => {
                notifications.show('Error Updating product!', {
                    severity: 'error',
                });
            });
    };

    const values = useWatch(formContext) as Product;

    return (
        <FormContainer onSuccess={onSubmit} formContext={formContext}>
            <Card>
                <CardHeader title="Product Details" />
                <CardContent>
                    <Stack gap={2}>
                        <BrandInput disabled={!editable} />
                        <ProductLineInput disabled={!editable} />

                        <TextFieldElement
                            fullWidth
                            name="name"
                            disabled={!editable}
                            label="Name"
                        />
                        <TextFieldElement
                            fullWidth
                            name="description"
                            disabled={!editable}
                            label="Description"
                        />

                        <TextFieldElement
                            fullWidth
                            name="volumeMl"
                            disabled={!editable}
                            label="Volume (ml)"
                            type="number"
                            slotProps={{
                                htmlInput: {
                                    min: 0,
                                    max: 100,
                                    step: 1,
                                },
                                input: {
                                    endAdornment: 'ml',
                                },
                            }}
                        />

                        <CheckboxElement
                            label="Is Alcoholic"
                            name="isAlcoholic"
                            disabled={!editable}
                        />

                        {values.isAlcoholic && (
                            <TextFieldElement
                                label="ABV Percentage"
                                name="abvPercentage"
                                fullWidth
                                disabled={!editable}
                                type="number"
                                slotProps={{
                                    htmlInput: {
                                        min: 0,
                                        max: 100,
                                    },
                                    input: {
                                        endAdornment: '%',
                                    },
                                }}
                            />
                        )}
                        <ProductTypeInput disabled={!editable} />
                        {values.productTypeId && (
                            <ProductSubtypeInput disabled={!editable} />
                        )}

                        <AutocompleteElement
                            label="Flavor"
                            name="flavor"
                            options={enumToList(FLAVOR_TYPES)}
                            autocompleteProps={{
                                disabled: !editable,
                            }}
                        />

                        <BaseProductInput disabled={!editable} />

                        <ImageField
                            label="Image"
                            name="image"
                            readOnly={!editable}
                            imagekitUploadProps={{
                                width: 200,
                                fileName: `${values.id}`,
                                folder: ImageKitMenuItemFolders.Product,
                                env: REACT_APP_ENV,
                                customMetadata: {
                                    [ImageKitCustomMetadataKeys.Id]: `${values['id']}`,
                                    [ImageKitCustomMetadataKeys.Type]:
                                        ImageKitTypes.Product,
                                    [ImageKitCustomMetadataKeys.Env]:
                                        REACT_APP_ENV,
                                },
                                tags: [
                                    `${values.brand?.name} ${values['name']}`,
                                    ImageKitTypes.Product,
                                    `${values['id']}`,
                                    REACT_APP_ENV,
                                ],
                            }}
                            imagekitProps={{
                                width: 300,
                                transformation: [
                                    {
                                        raw: ImageKitNamedTransforms.Web.cover,
                                    },
                                ],
                                loading: 'lazy',
                                lqip: {
                                    raw: ImageKitNamedTransforms.Web.coverLq,
                                },
                            }}
                        />
                    </Stack>
                </CardContent>
                <CardActions
                    sx={{
                        justifyContent: 'flex-end',
                        paddingRight: 3,
                        paddingBottom: 3,
                    }}
                >
                    <Can I="update" a="Alcohol">
                        <EditButtons
                            formContext={formContext}
                            handleCancel={handleCancel}
                            editable={editable}
                            editableActions={editableActions}
                        />
                    </Can>
                </CardActions>
            </Card>
        </FormContainer>
    );
};
